/* eslint-disable import/no-extraneous-dependencies */
import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import clsx from 'clsx';
import * as Yup from 'yup';
import { Formik, FormikProps } from 'formik';
import DOMPurify from 'isomorphic-dompurify';
import { useGoogleLogin } from '@react-oauth/google';
import { useRouter } from 'next/router';

import { useRollbar } from '@rollbar/react';
import AuthModel, { LoginFormData } from '../../../models/auth';
import { KNO_PRO_SUPPORT_EMAIL, LogInResponseCode, ROUTES } from '../../../Constants/variables';

import { AUTH_ERRORS } from '../../../Constants/ErrorMessages';
import AuthContext from '../../../AppContext/AuthProvider';
import Button from '../../Button';
import HelperText from '../../FormFields/HelperText';
import Image from '../../Image';
import { PageLoader } from '../../../HOC/Page/WithPage';
import PasswordField from '../../FormFields/PasswordField';
import SectionWrapper from '../../SectionWrapper';
import TextField from '../../FormFields/TextField';
import Typo from '../../Typo';
import { getFieldError } from '../../FormFields/Utils';
import googleLogo from '../../../../public/static/images/google-logo.svg';
import styles from './logInForm.module.sass';
import { useIsMobileViewport } from '../../../hooks/useIsMobileViewport';
// eslint-disable-next-line import/no-named-as-default-member
import useLoginActions from './useLoginActions';
import useErrorHandlers from '../../../hooks/useErrorHandlers';
import TwoFactor from './TwoFactor';

export const withLink = (child: React.ReactNode, link: string) => (
  <Button isBtn={false} linkToUrl={link} underline classes="has-text-weight-bold">
    {child}
  </Button>
);

interface LoginFormProps {
  title?: string;
  description?: React.ReactNode;
  subHeading?: string;
  signUpLink?: React.ReactNode;
}

const ValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email(AUTH_ERRORS.EMAIL_VALIDATION_NOT_MATCH)
    .required(AUTH_ERRORS.EMAIL_REQUIRED)
    .test('emailNotExist', AUTH_ERRORS.EMAIL_NOT_EXIST, async value => {
      if (!value) return true;
      const isExists = await AuthModel.isEmailExists(value);
      if (isExists === undefined) return true;
      return isExists;
    }),
  password: Yup.string().required(AUTH_ERRORS.LOGIN_PASSWORD_REQUIRED),
});

export const LogInForm: FC<LoginFormProps> = props => {
  const { title, description, subHeading, signUpLink } = props;
  const { isMobile } = useIsMobileViewport();
  const { handleLogin } = useLoginActions();
  return (
    <Formik<Partial<LoginFormData>>
      onSubmit={(val, helpers) => handleLogin.run({ values: val, formikHelpers: helpers })}
      initialValues={{}}
      validationSchema={ValidationSchema}
      enableReinitialize
    >
      {(formikProps: FormikProps<Partial<LoginFormData>>) => {
        const errors = {
          email: getFieldError('email', formikProps),
          password: getFieldError('password', formikProps),
        };
        return (
          <form onSubmit={formikProps.handleSubmit} id="log_in_form">
            <SectionWrapper
              hasBodyMargin={!isMobile}
              classes={styles.rootContainer}
              childClasses={clsx(styles.rootChildContainer)}
            >
              {title && (
                <Typo h1 classes={styles.heading}>
                  {title}
                </Typo>
              )}
              {description && (
                <div className={styles.subheading}>
                  <Typo medium>{description}</Typo>
                </div>
              )}

              <div className={styles.fieldsContainer}>
                {subHeading && (
                  <Typo h2 classes="mb-4">
                    {subHeading}
                  </Typo>
                )}
                <TextField
                  valueKey="email"
                  otherFieldProps={{
                    placeholder: 'Email Address',
                    onChange: formikProps.handleChange,
                  }}
                  rootClassName="mb-5"
                  placeHolderColorBlack
                  error={errors.email}
                  type="email"
                />
                <PasswordField
                  valueKey="password"
                  otherFieldProps={{
                    placeholder: 'Password',
                    onChange: formikProps.handleChange,
                    onBlur: formikProps.handleBlur,
                  }}
                  error={errors.password}
                  hidePasswordByDefault
                />
              </div>
              {handleLogin.status === 'ERROR' && (
                <div className={styles.errorBlock}>
                  <HelperText error>{handleLogin.message}</HelperText>
                  {handleLogin.message === AUTH_ERRORS.GENERIC_ERROR_500 && (
                    <div>
                      {' '}
                      <HelperText error>
                        Please try again or{' '}
                        <a href={`mailto:${KNO_PRO_SUPPORT_EMAIL}`} className="is-underlined">
                          report an issue
                        </a>{' '}
                        to support.
                      </HelperText>
                    </div>
                  )}
                </div>
              )}
              <Button
                type="submit"
                theme="Filled - Lime Green Gradient"
                classes={`${styles.loginBtn} ${
                  handleLogin.status === 'PROCESSING' && 'is-loading'
                }`}
                disabled={handleLogin.status === 'PROCESSING'}
              >
                Log In
              </Button>
              <div className={styles.linksContainer}>
                <Typo normal classes="is-block">
                  {withLink('I Need to Reset My Password', 'forgot-password')}
                </Typo>
                <Typo normal classes={`is-block ${isMobile ? 'mt-3' : 'mt-1'}`}>
                  {withLink('Contact Support', 'contact-us')}
                </Typo>
              </div>
              {signUpLink && (
                <div className="mt-3">
                  <Typo normal>{signUpLink}</Typo>
                </div>
              )}
            </SectionWrapper>
          </form>
        );
      }}
    </Formik>
  );
};

export const LogInFormSSO: FC<LoginFormProps> = props => {
  const { title, description, subHeading, signUpLink } = props;
  const { isMobile } = useIsMobileViewport();
  const { handleLogin } = useLoginActions();
  const [googleMode, setGoogleMode] = useState<boolean>(false);
  const [googleObject, setGoogleObject] = useState<any>();
  const [beforeGoogleRequest, setBeforeGoogleRequest] = useState<boolean>(true);
  const { ssoLoggedIn, redirected, setAuthState, twoFactor, setTwoFactor } =
    useContext(AuthContext);
  const [pageLoaded, setPageLoaded] = useState<boolean>(false);
  const [requestError, setRequestError] = useState<string | undefined>();
  const [errorApi, setErrorApi] = useState<string | undefined>();
  const { handleError } = useErrorHandlers();
  const LOGIN_LINK = `${ROUTES.LOGIN}/`;
  const router = useRouter();

  const pageLoading = (ssoLoggedIn && ssoLoggedIn === 'true') || !pageLoaded;
  const cancelLoading = useCallback(() => {
    setAuthState('sso', null);
    setAuthState('redirected', null);
  }, [setAuthState]);
  const removeParameters = useCallback(
    (urlParams: any) => {
      // Remove URL parameters for easy refresh, lint requires conditional here.
      urlParams.delete('code');
      urlParams.delete('scope');
      urlParams.delete('authuser');
      urlParams.delete('prompt');
      let urlParamsString = urlParams.toString();
      // account for partner redirect, add it to params
      if (urlParams.get('partner') !== 'true' && localStorage.getItem('partner') === 'true') {
        if (urlParamsString.length === 0) {
          urlParamsString = 'partner=true';
        } else {
          urlParamsString += '&partner=true';
        }
      }
      const url = urlParamsString.length > 0 ? `${LOGIN_LINK}?${urlParamsString}` : LOGIN_LINK;
      router.replace(url);
    },
    [LOGIN_LINK, router],
  );
  const rollbarCall = useRollbar();
  // Run when SSO is used
  useEffect(() => {
    if (beforeGoogleRequest) {
      const networkError = localStorage.getItem('networkError');
      if (networkError) {
        rollbarCall.info(`Something Went Wrong: ${networkError}`);
        localStorage.removeItem('networkError');
        handleError({
          code: 'ERR_NETWORK',
        });
      }
      setBeforeGoogleRequest(false);
      const queryParams = new URLSearchParams(window.location.search);
      // if User is coming from Partner redirect, this keeps it from
      // deleting partner localstorage items
      let signUpLinkPartner = 'sign-up';
      // At this point, localStorage partner's purpose is fulfilled, and needs to be removed,
      // so that if the user leaves login, it will properly remove the other partner localStorage values
      if (queryParams.get('partner') === 'true' || localStorage.getItem('partner') === 'true') {
        signUpLinkPartner = 'sign-up?partner=true';
      }
      setAuthState('redirected', localStorage.getItem('redirected'));
      if (localStorage.getItem('redirected') && !googleMode) {
        const code = queryParams.get('code');
        if (code) {
          AuthModel.getGoogleUser(code, 'login')
            .then(jwt => {
              removeParameters(queryParams);
              if (jwt.responseCode === LogInResponseCode.TWO_FACTOR_AUTH_REQUIRED) {
                setTwoFactor({
                  email: jwt.email as string,
                  isLocked: jwt.isLocked as boolean,
                  isNewCode: jwt.isNewCode as boolean,
                  sso: true,
                });
                return;
              }
              // Likely already vestigial, used for responding to Google response. Keeping for now.
              if (jwt.data && !jwt.data.message) {
                setGoogleObject(jwt.data);
                setGoogleMode(true);
                const val = { email: jwt.data.email, sso_id: jwt.data.id };
                handleLogin.run({ values: val });
                // Used for when logging in on backend with SSO
              } else if (jwt.responseCode && !jwt.data) {
                const val = { email: jwt.email };
                setAuthState('sso', 'true');
                handleLogin.run({ values: val, previousLogin: true });
                // When SSO user does not have registered email
              } else if (jwt.data && jwt.data.message === 'email_does_not_exist_sso') {
                cancelLoading();
                const purifiedError = DOMPurify.sanitize(
                  `This email has not yet been registered with KnoPro. Please <a class=${styles.errorLink} href="/${signUpLinkPartner}">Sign Up</a> and try again.`,
                );
                setRequestError(purifiedError);
              }
            })
            .catch(e => {
              cancelLoading();
              removeParameters(queryParams);
              const purifiedError = DOMPurify.sanitize(`${e.response?.data?.error}`);
              setRequestError(purifiedError);
              setErrorApi(e);
            });
          // If there is no code query parameter, user likely clicked back on SSO page
        } else {
          cancelLoading();
        }
      } else {
        cancelLoading();
      }
    }
  }, [
    errorApi,
    googleMode,
    handleLogin,
    beforeGoogleRequest,
    setAuthState,
    removeParameters,
    cancelLoading,
    rollbarCall,
    handleError,
    setTwoFactor,
  ]);
  // Run once page is fully loaded
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (
      queryParams.get('partner') === 'true' &&
      localStorage.getItem('partner') === 'true' &&
      requestError
    ) {
      localStorage.removeItem('partner');
    }
    // Fix for infinite loading screen
    if (queryParams.size === 0 && !redirected) {
      setAuthState('sso', null);
      setAuthState('redirected', null);
    }
    setPageLoaded(true);
    setAuthState('sso', localStorage.getItem('sso'));
    setAuthState('redirected', localStorage.getItem('redirected'));
  }, [pageLoaded, redirected, requestError, setAuthState]);
  useEffect(() => {
    const loadingDestroyer = () => {
      if ((localStorage.getItem('sso') || localStorage.getItem('redirected')) && !requestError) {
        setAuthState('sso', null);
        setAuthState('redirected', null);
        setRequestError('Your request appears to be taking awhile. Please try again later.');
      }
    };
    setTimeout(() => {
      loadingDestroyer();
    }, 30000);
  }, [redirected, requestError, setAuthState, ssoLoggedIn]);
  const googleRequest = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: process.env.NEXT_PUBLIC_GOOGLE_LOG_IN,
  });

  if (twoFactor !== null) {
    return <TwoFactor />;
  }

  return (
    <Formik<Partial<LoginFormData>>
      onSubmit={(val, helpers) => {
        if (googleMode) {
          val.sso_id = googleObject.id;
        }
        handleLogin.run({ values: val, formikHelpers: helpers });
      }}
      initialValues={{}}
      validationSchema={ValidationSchema}
      enableReinitialize
    >
      {(formikProps: FormikProps<Partial<LoginFormData>>) => {
        const errors = {
          email: getFieldError('email', formikProps),
          password: getFieldError('password', formikProps),
          google: getFieldError('google', formikProps),
        };
        return (
          <form onSubmit={formikProps.handleSubmit} id={styles.log_in_sso_form}>
            {pageLoading && <PageLoader />}
            <SectionWrapper
              hasBodyMargin={!isMobile}
              classes={pageLoading ? styles.hidden : styles.rootContainer}
              childClasses={clsx(styles.rootChildContainer)}
            >
              <SectionWrapper classes={styles.buttonHolder}>
                {requestError && (
                  <div
                    className={styles.requestError}
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{ __html: requestError }}
                  />
                )}
                <Button
                  onClick={() => {
                    setAuthState('sso', 'true');
                    setAuthState('redirected', 'true');
                    setRequestError(undefined);
                    localStorage.setItem('partner', 'true');
                    googleRequest();
                  }}
                  classes={styles.authButton}
                >
                  <Image image={googleLogo} width={20} height={30} classes={styles.googleLogo} />
                  Continue with Google
                </Button>
              </SectionWrapper>
              {title && (
                <Typo h1 classes={styles.heading}>
                  {title}
                </Typo>
              )}
              {description && (
                <div className={styles.subheading}>
                  <Typo medium>{description}</Typo>
                </div>
              )}

              <div className={styles.fieldsContainer}>
                {subHeading && (
                  <Typo h4 classes="mb-5">
                    {subHeading}
                  </Typo>
                )}
                <TextField
                  valueKey="email"
                  otherFieldProps={{
                    placeholder: 'Email Address',
                    onChange: formikProps.handleChange,
                  }}
                  rootClassName="mb-5"
                  placeHolderColorBlack
                  error={errors.email}
                  type="email"
                />
                <PasswordField
                  valueKey="password"
                  otherFieldProps={{
                    placeholder: 'Password',
                    onChange: formikProps.handleChange,
                    onBlur: formikProps.handleBlur,
                  }}
                  error={errors.password}
                  hidePasswordByDefault
                />
              </div>
              {handleLogin.status === 'ERROR' && (
                <div className={styles.errorBlock}>
                  <HelperText error>{handleLogin.message}</HelperText>
                  {handleLogin.message === AUTH_ERRORS.GENERIC_ERROR_500 && (
                    <div>
                      {' '}
                      <HelperText error>
                        Please try again or{' '}
                        <a href={`mailto:${KNO_PRO_SUPPORT_EMAIL}`} className="is-underlined">
                          report an issue
                        </a>{' '}
                        to support.
                      </HelperText>
                    </div>
                  )}
                </div>
              )}
              <Button
                type="submit"
                theme="Filled - Lime Green Gradient"
                classes={`${styles.loginBtn} ${
                  handleLogin.status === 'PROCESSING' && 'is-loading'
                }`}
                disabled={handleLogin.status === 'PROCESSING'}
              >
                Log In
              </Button>
              <div className={styles.linksContainer}>
                <Typo normal classes="is-block">
                  {withLink('I Need to Reset My Password', 'forgot-password')}
                </Typo>
                <Typo normal classes={`is-block ${isMobile ? 'mt-3' : 'mt-1'}`}>
                  {withLink('Contact Support', 'contact-us')}
                </Typo>
              </div>
              {signUpLink && (
                <div className="mt-3">
                  <Typo normal>{signUpLink}</Typo>
                </div>
              )}
            </SectionWrapper>
          </form>
        );
      }}
    </Formik>
  );
};
